import styles from "../chat-bubble.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import Image from "next/image";
import { IoDocumentText } from "react-icons/io5";
import "swiper/css";
import { useMemo, useState } from "react";
import MediaViewerDialog, { ViewerItem } from "../media-viewer-dialog";
import {
  getMediaArray,
  MediaPayload,
} from "../../../../../../../../lib/chat-store/types";
import { FaFilePdf, FaFileExcel, FaFileWord } from "react-icons/fa6";
import { getMedia } from "@/lib/media/shared";
import MediaWrapper from "@/components/media-wrapper/media-wrapper";

export default function MediaBubble({
  payload,
  timestamp,
  sender_id,
  receiverCompleteName,
  receiverPhotoUrl,
}: {
  payload: MediaPayload;
  timestamp: number;
  sender_id: string;
  receiverCompleteName: string | undefined;
  receiverPhotoUrl: string | undefined;
}) {
  const { userData } = useUserAuthDataContext();
  const isMine = sender_id === userData.sub;
  const senderName =
    sender_id === userData.sub ? userData.complete_name : receiverCompleteName;
  const senderPhoto =
    sender_id === userData.sub ? userData.user_photo : receiverPhotoUrl;
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const mediaItems = getMediaArray(payload);
  const resolvedItems = useMemo(() => {
    return mediaItems.map((m) => getMedia(m, "lg"));
  }, [mediaItems]);
  const visibleItems = mediaItems.slice(0, 4);
  // 🔥 single source of truth
  const visibleResolved = resolvedItems.slice(0, 4);
  // keep ONLY images for viewer (no breaking change)
  const viewerItems = useMemo(() => {
    return resolvedItems
      .map((m) => {
        if (!m?.src) return null;
        if (m.type !== "image" && m.type !== "video") return null;

        return {
          url: m.src,
          type: m.type,
          thumbnailUrl: m.preview || null,
        };
      })
      .filter((item): item is ViewerItem => Boolean(item));
  }, [resolvedItems]);

  const extraCount = mediaItems.length - 4;

  const getLayoutClass = () => {
    switch (visibleItems.length) {
      case 1:
        return styles.one;
      case 2:
        return styles.two;
      case 3:
        return styles.three;
      default:
        return styles.four;
    }
  };
  return (
    <div
      className={`${styles.row} ${isMine ? styles.mine : styles.theirs} ${styles.mediaBubble}`}
    >
      <div className={styles.bubbleWrapper}>
        <div className={styles.infoAndTextWrapper}>
          <span className={styles.userInfo}>
            <span className={styles.userName}>{senderName}</span>
            <Image
              className={styles.avatar}
              src={senderPhoto || "/broken_image.webp"}
              width={24}
              height={24}
              alt={`${senderPhoto || "User"} profile photo`}
            />
          </span>
          {visibleItems.length > 0 && (
            <div className={`${styles.mediaGrid} ${getLayoutClass()}`}>
              {visibleItems.map((media, index) => {
                const resolved = visibleResolved[index];
                const meta = getFileMeta(media.OriginalFileName);

                return (
                  <div
                    key={media.MediaId + index}
                    className={`${styles.mediaItem} item`}
                    onClick={() => {
                      if (!resolved?.src) return;

                      if (
                        resolved.type === "image" ||
                        resolved.type === "video"
                      ) {
                        const viewerIdx = viewerItems.findIndex(
                          (item) => item.url === resolved.src,
                        );

                        setViewerIndex(viewerIdx !== -1 ? viewerIdx : 0);
                        setViewerOpen(true);
                      } else {
                        window.open(resolved.src, "_blank");
                      }
                    }}
                  >
                    <MediaWrapper media={media} theme="dark">
                      {resolved?.type === "image" && (
                        <Image
                          src={resolved.src}
                          fill
                          sizes="400px"
                          style={{
                            objectFit: "cover",
                            objectPosition: "center",
                          }}
                          alt={media.OriginalFileName || "media"}
                          className={styles.mediaImage}
                        />
                      )}
                      {resolved?.type === "video" && (
                        <div className={styles.mediaImage}>
                          <Image
                            src={resolved.preview || resolved.src}
                            fill
                            sizes="400px"
                            style={{
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                            alt="video thumbnail"
                            className={styles.mediaImage}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              fontSize: "24px",
                              color: "white",
                              pointerEvents: "none",
                            }}
                          >
                            ▶
                          </div>
                        </div>
                      )}
                      {resolved?.type === "document" && (
                        <div className={styles.documentItem}>
                          <div className={styles.documentIconWrapper}>
                            {getFileIcon(meta.type)}
                          </div>

                          <div className={styles.documentInfo}>
                            <div className={styles.documentName}>
                              {media.OriginalFileName}
                            </div>
                            <div className={styles.documentMeta}>
                              {meta.ext}
                            </div>
                          </div>
                        </div>
                      )}
                    </MediaWrapper>
                    {/* +X overlay */}
                    {index === 3 && extraCount > 0 && (
                      <div className={styles.moreOverlay}>+{extraCount}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <span className={styles.text}>{payload["text"] ?? ""}</span>
        </div>
        <div className={styles.messageInfo}>
          {/* {isMine && <DoneAll className={styles.checkmark} />} */}
          <span className={styles.time}>{formatTime(timestamp)}</span>
        </div>
      </div>
      {viewerOpen && (
        <MediaViewerDialog
          items={viewerItems}
          activeIndex={viewerIndex}
          setActiveIndex={setViewerIndex}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}

function getFileMeta(fileName?: string) {
  if (!fileName) return { ext: "", type: "other" };

  const ext = fileName.split(".").pop()?.toLowerCase() || "";

  if (ext === "pdf") return { ext: "PDF", type: "pdf" };
  if (ext === "docx" || ext === "doc") return { ext: "DOCX", type: "word" };
  if (ext === "xlsx" || ext === "xls") return { ext: "XLSX", type: "excel" };

  return { ext: ext.toUpperCase(), type: "other" };
}

function getFileIcon(type: string) {
  switch (type) {
    case "pdf":
      return <FaFilePdf className={styles.fileIconPdf} />;
    case "word":
      return <FaFileWord className={styles.fileIconWord} />;
    case "excel":
      return <FaFileExcel className={styles.fileIconExcel} />;
    default:
      return <IoDocumentText className={styles.fileIconDefault} />;
  }
}
